import { useState, useEffect, useMemo } from "react";
import {
  Plus, Search, Pencil, Trash2, Building2, MapPin, Phone, User,
  CircleCheck, CircleX, Truck, ArrowUpDown, AlertTriangle,
  FileText, UploadCloud, Loader2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { storageService } from "@/lib/storage";
import { venuesApi, modelsApi } from "@/lib/unifiedApi";
import { logActivity } from "@/lib/activityLog";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  name: "", map_link: "", address: "", contact_name: "", contact_phone: "",
  loading_dock: "", vehicle_height_limit: "", elevator_size: "",
  restrictions: "", note: "", model_fit: {}, active: true,
  images: [], files: [],
};

function toForm(v) {
  if (!v) return { ...EMPTY, images: [], files: [] };
  return {
    name: v.name ?? "", map_link: v.map_link ?? "", address: v.address ?? "",
    contact_name: v.contact_name ?? "", contact_phone: v.contact_phone ?? "",
    loading_dock: v.loading_dock ?? "", vehicle_height_limit: v.vehicle_height_limit ?? "",
    elevator_size: v.elevator_size ?? "", restrictions: v.restrictions ?? "",
    note: v.note ?? "", model_fit: { ...(v.model_fit || {}) }, active: v.active !== false,
    images: [...(v.images || [])], files: [...(v.files || [])],
  };
}

// ─── Add/Edit dialog ────────────────────────────────────────
function VenueDialog({ open, onClose, venue, models }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  useEffect(() => { setForm(toForm(venue)); }, [venue, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function uploadFiles(fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) { toast.error(`${file.name} ใหญ่เกิน 20MB`); continue; }
        const res = await storageService.uploadFile(file, `venues/${Date.now()}`);
        const item = { url: res.url, name: res.name, path: res.path };
        if (/^image\//.test(file.type)) setForm((f) => ({ ...f, images: [...f.images, item] }));
        else setForm((f) => ({ ...f, files: [...f.files, item] }));
      }
    } catch (e) { console.error(e); toast.error("อัปโหลดไม่สำเร็จ"); }
    finally { setUploading(false); }
  }
  const rmImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const rmFile = (i) => setForm((f) => ({ ...f, files: f.files.filter((_, idx) => idx !== i) }));
  const setFit = (modelName, status) =>
    setForm((f) => {
      const model_fit = { ...f.model_fit };
      if (model_fit[modelName] === status) delete model_fit[modelName]; // toggle off
      else model_fit[modelName] = status;
      return { ...f, model_fit };
    });

  async function save() {
    if (!form.name.trim()) { toast.error("กรุณากรอกชื่อสถานที่"); return; }
    setSaving(true);
    try {
      const data = {
        name: form.name.trim(), map_link: form.map_link.trim(), address: form.address.trim(),
        contact_name: form.contact_name.trim(), contact_phone: form.contact_phone.trim(),
        loading_dock: form.loading_dock.trim(), vehicle_height_limit: form.vehicle_height_limit.trim(),
        elevator_size: form.elevator_size.trim(), restrictions: form.restrictions.trim(),
        note: form.note.trim(), model_fit: form.model_fit, active: form.active,
        images: form.images, files: form.files,
      };
      if (venue?.id) {
        await venuesApi.update(venue.id, data);
        logActivity(user?.id, user?.name, "venue_update", `แก้ไขสถานที่ ${data.name}`);
        toast.success("บันทึกแล้ว");
      } else {
        await venuesApi.create(data);
        logActivity(user?.id, user?.name, "venue_create", `เพิ่มสถานที่ ${data.name}`);
        toast.success("เพิ่มสถานที่แล้ว");
      }
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  }

  const fld = "bg-white border-slate-200 text-slate-800";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{venue?.id ? "แก้ไขสถานที่" : "เพิ่มสถานที่"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-slate-500">ชื่อสถานที่ <span className="text-red-500">*</span></Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="เช่น Four Seasons Bangkok" className={fld} />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">Google Map Link</Label>
            <Input value={form.map_link} onChange={(e) => set("map_link", e.target.value)}
              placeholder="https://maps.app.goo.gl/..." className={fld} />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">ที่อยู่</Label>
            <textarea value={form.address} onChange={(e) => set("address", e.target.value)}
              rows={2} placeholder="ที่อยู่สถานที่"
              className="w-full rounded-md bg-white border border-slate-200 text-slate-800 text-sm p-2 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-slate-500">ผู้ประสานงาน</Label>
              <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)}
                placeholder="ชื่อผู้ติดต่อ" className={fld} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500">เบอร์ติดต่อ</Label>
              <Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)}
                placeholder="0xx-xxx-xxxx" className={fld} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-slate-500">จุดโหลด / ชั้น</Label>
              <Input value={form.loading_dock} onChange={(e) => set("loading_dock", e.target.value)}
                placeholder="เช่น ชั้น B2 ประตูหลัง" className={fld} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500">ความสูงรถ (limit)</Label>
              <Input value={form.vehicle_height_limit} onChange={(e) => set("vehicle_height_limit", e.target.value)}
                placeholder="เช่น ไม่เกิน 2.1 ม." className={fld} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500">ขนาดลิฟต์</Label>
              <Input value={form.elevator_size} onChange={(e) => set("elevator_size", e.target.value)}
                placeholder="กว้าง×ลึก×สูง" className={fld} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">ข้อจำกัด</Label>
            <textarea value={form.restrictions} onChange={(e) => set("restrictions", e.target.value)}
              rows={2} placeholder="ข้อจำกัดต่างๆ เช่น ต้องแจ้งล่วงหน้า, เวลาเข้าโหลด"
              className="w-full rounded-md bg-white border border-slate-200 text-slate-800 text-sm p-2 resize-none" />
          </div>

          {/* Model fit */}
          <div className="space-y-2 border-t border-slate-200 pt-3">
            <Label className="text-slate-500">โมเดลตู้ — เข้าได้ / เข้าไม่ได้</Label>
            <div className="space-y-1.5">
              {models.length === 0 && <div className="text-sm text-slate-400">ยังไม่มีโมเดลในระบบ</div>}
              {models.map((m) => {
                const st = form.model_fit[m.model_name];
                return (
                  <div key={m.id} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-700 truncate">{m.model_name}</span>
                    <div className="flex gap-1 flex-shrink-0">
                      <button type="button" onClick={() => setFit(m.model_name, "ok")}
                        className={`px-2.5 py-1 rounded-md text-xs border ${st === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"}`}>
                        เข้าได้
                      </button>
                      <button type="button" onClick={() => setFit(m.model_name, "no")}
                        className={`px-2.5 py-1 rounded-md text-xs border ${st === "no" ? "bg-rose-50 text-rose-700 border-rose-300" : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"}`}>
                        เข้าไม่ได้
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Attachments: images + PDF/docs */}
          <div className="space-y-2 border-t border-slate-200 pt-3">
            <Label className="text-slate-500">รูปภาพ / เอกสาร (PDF) — แนบได้หลายไฟล์</Label>

            {form.images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {form.images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={img.url} alt={img.name || ""} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                    <button type="button" onClick={() => rmImage(i)}
                      className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-0.5 shadow">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {form.files.length > 0 && (
              <div className="space-y-1.5">
                {form.files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <FileText size={15} className="text-blue-600 flex-shrink-0" />
                    <a href={f.url} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-slate-700 truncate flex-1 hover:text-blue-600">{f.name}</a>
                    <button type="button" onClick={() => rmFile(i)} className="text-slate-400 hover:text-rose-500 flex-shrink-0">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 cursor-pointer">
              <input type="file" accept="image/*,application/pdf" multiple className="hidden"
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} disabled={uploading} />
              {uploading ? <><Loader2 size={14} className="animate-spin" />กำลังอัปโหลด...</> : <><UploadCloud size={14} />เพิ่มรูป / ไฟล์</>}
            </label>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">หมายเหตุ</Label>
            <Input value={form.note} onChange={(e) => set("note", e.target.value)} className={fld} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-500">ยกเลิก</Button>
          <Button onClick={save} disabled={saving || uploading} style={{ backgroundColor: "#2563eb", color: "#fff" }}>
            {saving ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Venue card ─────────────────────────────────────────────
function VenueCard({ venue, onEdit, onDelete }) {
  const fit = venue.model_fit || {};
  const ok = Object.keys(fit).filter((k) => fit[k] === "ok");
  const no = Object.keys(fit).filter((k) => fit[k] === "no");
  const info = [
    { icon: Truck, label: "จุดโหลด", value: venue.loading_dock },
    { icon: ArrowUpDown, label: "ความสูงรถ", value: venue.vehicle_height_limit },
    { icon: Building2, label: "ขนาดลิฟต์", value: venue.elevator_size },
    { icon: AlertTriangle, label: "ข้อจำกัด", value: venue.restrictions },
  ];
  const images = venue.images || [];
  const files = venue.files || [];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="font-medium text-slate-800 flex items-center gap-2 min-w-0">
          <Building2 size={17} className="text-blue-600 flex-shrink-0" />
          <span className="truncate">{venue.name}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {venue.map_link && (
            <a href={venue.map_link} target="_blank" rel="noopener noreferrer"
              className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600" title="Google Map">
              <MapPin size={15} />
            </a>
          )}
          <button onClick={() => onEdit(venue)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400" title="แก้ไข">
            <Pencil size={15} />
          </button>
          <button onClick={() => onDelete(venue)} className="p-1.5 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-500" title="ลบ">
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {(venue.address || venue.contact_name || venue.contact_phone) && (
        <div className="text-xs text-slate-500 mb-2 space-y-0.5">
          {venue.address && <div className="flex items-start gap-1"><MapPin size={11} className="mt-0.5 flex-shrink-0" />{venue.address}</div>}
          {(venue.contact_name || venue.contact_phone) && (
            <div className="flex items-center gap-2">
              {venue.contact_name && <span className="flex items-center gap-1"><User size={11} />{venue.contact_name}</span>}
              {venue.contact_phone && <span className="flex items-center gap-1"><Phone size={11} />{venue.contact_phone}</span>}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-3">
        {info.map(({ icon: Icon, label, value }) => value ? (
          <div key={label}>
            <div className="text-[11px] text-slate-400 flex items-center gap-1"><Icon size={11} />{label}</div>
            <div className="text-slate-700">{value}</div>
          </div>
        ) : null)}
      </div>

      {(ok.length > 0 || no.length > 0) && (
        <div className="border-t border-slate-100 pt-2.5 space-y-1.5">
          {ok.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-slate-500 flex items-center gap-1"><CircleCheck size={13} className="text-emerald-600" />เข้าได้:</span>
              {ok.map((n) => <span key={n} className="text-xs bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-0.5">{n}</span>)}
            </div>
          )}
          {no.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-slate-500 flex items-center gap-1"><CircleX size={13} className="text-rose-600" />เข้าไม่ได้:</span>
              {no.map((n) => <span key={n} className="text-xs bg-rose-50 text-rose-700 rounded-full px-2.5 py-0.5">{n}</span>)}
            </div>
          )}
        </div>
      )}

      {/* Attachments */}
      {(images.length > 0 || files.length > 0) && (
        <div className="border-t border-slate-100 pt-2.5 mt-2.5 space-y-2">
          {images.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {images.map((img, i) => (
                <a key={i} href={img.url} target="_blank" rel="noopener noreferrer" title={img.name || ""}>
                  <img src={img.url} alt={img.name || ""} className="w-16 h-16 object-cover rounded-lg border border-slate-200 hover:opacity-90" />
                </a>
              ))}
            </div>
          )}
          {files.map((f, i) => (
            <a key={i} href={f.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 hover:underline">
              <FileText size={13} className="flex-shrink-0" />
              <span className="truncate">{f.name}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────
export default function VenuePage() {
  const [venues, setVenues] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    const unsub = venuesApi.subscribe((data) => {
      data.sort((a, b) => (a.name || "").localeCompare(b.name || "", "th"));
      setVenues(data);
      setLoading(false);
    }, () => setLoading(false));
    const unsubM = modelsApi.subscribe((data) => {
      data.sort((a, b) => (a.model_name || "").localeCompare(b.model_name || "", "th"));
      setModels(data.filter((m) => m.model_name));
    }, () => {});
    return () => { unsub(); unsubM(); };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return venues;
    return venues.filter((v) =>
      (v.name || "").toLowerCase().includes(q) || (v.address || "").toLowerCase().includes(q)
    );
  }, [venues, search]);

  async function remove(v) {
    if (!window.confirm(`ลบสถานที่ "${v.name}" ?`)) return;
    try {
      await venuesApi.delete(v.id);
      logActivity(user?.id, user?.name, "venue_delete", `ลบสถานที่ ${v.name}`);
      toast.success("ลบแล้ว");
    } catch { toast.error("ลบไม่สำเร็จ"); }
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Building2 size={24} className="text-blue-600" />Venue
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">สถานที่จัดงาน + ข้อมูล logistics (จุดโหลด / ลิฟต์ / โมเดลตู้ที่เข้าได้)</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }}
          style={{ backgroundColor: "#2563eb", color: "#fff" }} className="font-semibold">
          <Plus size={16} className="mr-1" />เพิ่มสถานที่
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหาชื่อสถานที่ / ที่อยู่"
          className="pl-9 bg-white border-slate-200 text-slate-800" />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center text-slate-400 py-16">กำลังโหลด...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl py-16 text-center text-slate-400">
          {venues.length === 0 ? 'ยังไม่มีสถานที่ — กด "เพิ่มสถานที่" เพื่อเริ่มต้น' : "ไม่พบสถานที่ที่ค้นหา"}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((v) => (
            <VenueCard key={v.id} venue={v}
              onEdit={(x) => { setEditing(x); setDialogOpen(true); }}
              onDelete={remove} />
          ))}
        </div>
      )}

      <VenueDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        venue={editing}
        models={models}
      />
    </div>
  );
}
